import React from "react";
import { Box, Container, Typography, Button } from "@mui/material";
import { useTheme } from "@emotion/react";
import { Link as RouterLink } from "react-router-dom";
import { getPageRoute } from "src/services";

/**
 * Reusable closing-CTA section. Two visual sizes:
 *
 *   variant="hero"    Full-bleed section with a large headline, radial green
 *                     glow, big pill CTA, and fine-print. Use once per page
 *                     as the last thing above the footer.
 *
 *   variant="compact" A tighter version for the end of individual sections
 *                     (What's Different, Values, TeacherToCoach, etc). No
 *                     glow, smaller type, still centered.
 *
 * Pass `theme="dark"` for a black-background version (matches Privacy, Terms,
 * Report). Copy is fully driven by props so we can tune the language per
 * page (waitlist vs. report vs. district partnership) while keeping the
 * visual language consistent.
 */
const ClosingCTA = ({
  variant = "hero",
  theme: themeVariant = "light",
  headline,
  headlineAccent,
  subtitle,
  ctaLabel = "Join the waitlist →",
  ctaTo,
  finePrint,
  bgcolor,
}) => {
  const theme = useTheme();
  const isHero = variant === "hero";
  const isDark = themeVariant === "dark";
  const resolvedCtaTo = ctaTo || getPageRoute("waitlist", "WAITLIST");
  const headlineColor = isDark
    ? theme.palette.paper.primary
    : theme.palette.text.primary;
  const subtitleColor = isDark
    ? "rgba(255,255,255,0.7)"
    : theme.palette.text.secondary;
  const finePrintColor = isDark
    ? "rgba(255,255,255,0.55)"
    : theme.palette.text.secondary;
  const defaultBg = isDark ? "#000000" : theme.palette.paper.primary;

  return (
    <Box
      component="section"
      sx={{
        pt: isHero ? { xs: 10, md: 15 } : { xs: 6, md: 8 },
        pb: isHero ? { xs: 12, md: 18 } : { xs: 6, md: 8 },
        bgcolor: bgcolor || defaultBg,
        color: headlineColor,
        textAlign: "center",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {isHero && (
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 900,
            height: 900,
            borderRadius: "50%",
            background: isDark
              ? "radial-gradient(circle, rgba(32,156,51,0.2) 0%, transparent 70%)"
              : "radial-gradient(circle, rgba(32,156,51,0.08) 0%, transparent 70%)",
            zIndex: 0,
          }}
        />
      )}

      <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
        <Typography
          component="h2"
          sx={{
            fontSize: isHero
              ? { xs: "2.25rem", md: "4rem" }
              : { xs: "1.75rem", md: "2.5rem" },
            fontWeight: 800,
            letterSpacing: "-0.03em",
            lineHeight: 1.06,
            color: headlineColor,
            maxWidth: 780,
            mx: "auto",
            textWrap: "balance",
          }}
        >
          {headline}
          {headlineAccent && (
            <>
              {" "}
              <Box
                component="span"
                sx={{ color: theme.palette.success.main }}
              >
                {headlineAccent}
              </Box>
            </>
          )}
          .
        </Typography>

        {subtitle && (
          <Typography
            sx={{
              fontSize: isHero
                ? { xs: "1.0625rem", md: "1.1875rem" }
                : { xs: "0.9375rem", md: "1rem" },
              color: subtitleColor,
              mt: 2.5,
              maxWidth: 560,
              mx: "auto",
              lineHeight: 1.5,
            }}
          >
            {subtitle}
          </Typography>
        )}

        <Box sx={{ mt: isHero ? 5.5 : 4 }}>
          <Button
            component={RouterLink}
            to={resolvedCtaTo}
            variant="contained"
            sx={{
              bgcolor: theme.palette.success.main,
              color: theme.palette.paper.primary,
              px: isHero ? { xs: 5, md: 6.5 } : { xs: 3.5, md: 4.5 },
              py: isHero ? { xs: 2.25, md: 2.75 } : { xs: 1.5, md: 1.75 },
              fontSize: isHero
                ? { xs: "1.0625rem", md: "1.25rem" }
                : { xs: "0.9375rem", md: "1rem" },
              fontWeight: 700,
              textTransform: "none",
              borderRadius: 999,
              whiteSpace: "nowrap",
              boxShadow: isHero
                ? "0 10px 32px rgba(32,156,51,0.35)"
                : "0 6px 20px rgba(32,156,51,0.25)",
              "&:hover": {
                bgcolor: "#15803d",
                transform: "translateY(-2px)",
                boxShadow: isHero
                  ? "0 12px 36px rgba(32,156,51,0.45)"
                  : "0 8px 24px rgba(32,156,51,0.35)",
              },
              transition:
                "background-color 0.15s, box-shadow 0.15s, transform 0.15s",
            }}
          >
            {ctaLabel}
          </Button>
        </Box>

        {finePrint && (
          <Typography
            sx={{
              fontSize: "0.875rem",
              color: finePrintColor,
              mt: 3,
              opacity: isDark ? 1 : 0.7,
              maxWidth: 560,
              mx: "auto",
            }}
          >
            {finePrint}
          </Typography>
        )}
      </Container>
    </Box>
  );
};

export default ClosingCTA;
